import { useState, useEffect, useCallback, useRef } from 'react';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/ArrowsCounterClockwise.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/ArrowsOutIn.svg';
import readMoreIcon from '../../../assets/read_more.svg';
import api from '../../../api';
import { useI18n } from '../../../i18n';

const PER_PAGE = 10;

const getSourceName = (feed) => {
    if (!feed) return '';
    return feed.source && typeof feed.source === 'object'
        ? feed.source.name
        : feed.source || 'News Feed';
};

const ArticleSkeleton = () => (
    <div className="news-article-skeleton">
        <div className="nfw-skel-thumb" />
        <div className="nfw-skel-content">
            <div className="nfw-skel-line nfw-skel-title" />
            <div className="nfw-skel-line nfw-skel-excerpt" />
            <div className="nfw-skel-line nfw-skel-date" />
        </div>
    </div>
);

const toLocaleCode = (locale) => {
    if (locale === 'en') return 'en-GB';
    return locale || 'en';
};

const formatPublishedAt = (value, locale) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(toLocaleCode(locale), {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);
};

/* Shared article list — used in both compact widget and expanded panel */
const ArticleList = ({ feeds, loadingMore, hasMore, onArticleClick, scrollRef, onScroll, initialLoading, locale, t }) => (
    <div className="widget-body news-feed-body" ref={scrollRef} onScroll={onScroll}>
        {initialLoading ? (
            <><ArticleSkeleton /><ArticleSkeleton /><ArticleSkeleton /></>
        ) : feeds.length === 0 ? (
            <div className="nfw-empty">{t('pages.news_feed.no_news')}</div>
        ) : (
            <>
                {feeds.map((feed, index) => (
                    <div
                        key={feed.id ?? index}
                        className="news-article"
                        onClick={() => onArticleClick(feed)}
                    >
                        <img
                            src={feed.urlToImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(getSourceName(feed))}&background=0D8ABC&color=fff&size=110`}
                            alt={feed.title}
                            className="news-thumbnail"
                            onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=News&background=0D8ABC&color=fff&size=110'; }}
                        />
                        <div className="news-content">
                            <div className="news-title">{feed.title}</div>
                            <div className="news-excerpt">
                                {feed.description || feed.content || t('pages.news_feed.no_description')}
                            </div>
                            <div className="news-date">{formatPublishedAt(feed.publishedAt, locale)}</div>
                        </div>
                    </div>
                ))}
                {loadingMore && (
                    <div className="nfw-load-more-spinner"><div className="nfw-spinner" /></div>
                )}
                {!hasMore && feeds.length > 0 && (
                    <div className="nfw-end-of-feed">{t('pages.news_feed.no_more')}</div>
                )}
            </>
        )}
    </div>
);

const NewsFeedWidget = () => {
    const { locale, t } = useI18n();
    const [feeds, setFeeds]                       = useState([]);
    const [page, setPage]                         = useState(1);
    const [hasMore, setHasMore]                   = useState(true);
    const [initialLoading, setInitialLoading]     = useState(true);
    const [loadingMore, setLoadingMore]           = useState(false);
    const [isExpanded, setIsExpanded]             = useState(false);
    const [selectedArticle, setSelectedArticle]   = useState(null);

    const compactBodyRef  = useRef(null);
    const expandedBodyRef = useRef(null);
    const isFetching      = useRef(false);

    const fetchPage = useCallback(async (pageNum, reset = false) => {
        if (isFetching.current) return;
        isFetching.current = true;

        if (pageNum === 1) setInitialLoading(true);
        else setLoadingMore(true);

        try {
            const response = await api.get(`/v1/news?page=${pageNum}&per_page=${PER_PAGE}`);
            if (response.status) {
                const { articles, meta } = response.data;
                setFeeds(prev => (reset || pageNum === 1) ? articles : [...prev, ...articles]);
                setHasMore(meta.has_more);
                setPage(pageNum);
            }
        } catch (e) {
            // interceptor handles error toast
        } finally {
            setInitialLoading(false);
            setLoadingMore(false);
            isFetching.current = false;
        }
    }, []);

    useEffect(() => { fetchPage(1, true); }, [fetchPage]);

    const makeScrollHandler = useCallback((ref) => () => {
        const el = ref.current;
        if (!el || loadingMore || !hasMore || isFetching.current) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            fetchPage(page + 1);
        }
    }, [fetchPage, hasMore, loadingMore, page]);

    const handleRefresh = useCallback(() => { fetchPage(1, true); }, [fetchPage]);

    const toggleExpand = useCallback(() => setIsExpanded(v => !v), []);

    return (
        <>
            {/* ── Compact widget ── */}
            <div className="widget-card news-feed-widget">
                <div className="widget-header news-feed-header">
                    <span className="widget-title news-feed-title">{t('navigation.news_feed')}</span>
                    <div className="header-right">
                        <RefreshIcon className="widget-icon news-feed-icon" onClick={handleRefresh} title={t('common.actions.refresh')} />
                        <ExpandIcon  className="widget-icon news-feed-icon" onClick={toggleExpand} title={t('common.actions.expand')} />
                    </div>
                </div>
                <ArticleList
                    feeds={feeds}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    initialLoading={initialLoading}
                    onArticleClick={setSelectedArticle}
                    scrollRef={compactBodyRef}
                    onScroll={makeScrollHandler(compactBodyRef)}
                    locale={locale}
                    t={t}
                />
            </div>

            {/* ── Expanded panel overlay ── */}
            {isExpanded && (
                <div className="nfw-expanded-overlay" onClick={toggleExpand}>
                    <div className="nfw-expanded-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="widget-header news-feed-header nfw-expanded-header">
                            <span className="widget-title news-feed-title">{t('navigation.news_feed')}</span>
                            <div className="header-right">
                                <RefreshIcon className="widget-icon news-feed-icon" onClick={handleRefresh} title={t('common.actions.refresh')} />
                                <ExpandIcon  className="widget-icon news-feed-icon nfw-collapse-icon" onClick={toggleExpand} title={t('common.actions.collapse')} />
                            </div>
                        </div>
                        <ArticleList
                            feeds={feeds}
                            loadingMore={loadingMore}
                            hasMore={hasMore}
                            initialLoading={initialLoading}
                            onArticleClick={setSelectedArticle}
                            scrollRef={expandedBodyRef}
                            onScroll={makeScrollHandler(expandedBodyRef)}
                            locale={locale}
                            t={t}
                        />
                    </div>
                </div>
            )}

            {/* ── Article detail modal ── */}
            {selectedArticle && (
                <div className="nfw-modal-overlay" onClick={() => setSelectedArticle(null)}>
                    <div className="nfw-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="nfw-modal-close" onClick={() => setSelectedArticle(null)}>✕</button>
                        {selectedArticle.urlToImage && (
                            <img
                                src={selectedArticle.urlToImage}
                                alt={selectedArticle.title}
                                className="nfw-modal-image"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        <h2 className="nfw-modal-title">{selectedArticle.title}</h2>
                        <div className="nfw-modal-meta">
                            {getSourceName(selectedArticle)}
                            {selectedArticle.publishedAt && ` · ${formatPublishedAt(selectedArticle.publishedAt, locale)}`}
                        </div>
                        <p className="nfw-modal-body">
                            {selectedArticle.content || selectedArticle.description || t('pages.news_feed.no_content')}
                        </p>
                        {selectedArticle.url && (
                            <a href={selectedArticle.url} target="_blank" rel="noopener noreferrer" className="nfw-modal-link">
                                {t('pages.news_feed.read_full', { source: getSourceName(selectedArticle) })}
                                <img src={readMoreIcon} alt="" className="read-more-icon" />
                            </a>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default NewsFeedWidget;
